import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { HeartHandshake } from "lucide-react";
import { IntakeFormData } from "./IntakeFormSchema";

interface SdohSectionProps {
  form: UseFormReturn<IntakeFormData>;
}

const SDOH_DOMAINS = [
  {
    id: "economic_stability",
    title: "Domain 1: Economic Stability",
    description: "Ability to meet basic needs including food, housing, and financial resources.",
    questions: [
      {
        id: "es_food_insecurity",
        text: "In the past 12 months, did you worry that your food would run out before you got money to buy more?",
        options: ["Never true", "Sometimes true", "Often true", "Decline to answer"],
      },
      {
        id: "es_utility_difficulty",
        text: "In the past 12 months, has a lack of money kept you from paying your utility bills (electric, gas, water)?",
        options: ["No", "Yes", "Already shut off", "Decline to answer"],
      },
      {
        id: "es_employment",
        text: "What is your current employment status?",
        options: ["Employed full-time or part-time", "Unemployed and looking for work", "Unemployed and not looking for work", "Retired or unable to work", "Decline to answer"],
      },
    ],
  },
  {
    id: "education_access",
    title: "Domain 2: Education Access and Quality",
    description: "Literacy, language, and educational attainment linked to health literacy.",
    questions: [
      {
        id: "eaq_education_level",
        text: "What is the highest level of education you have completed?",
        options: ["Less than high school", "High school diploma or GED", "Some college or technical school", "College degree or higher", "Decline to answer"],
      },
      {
        id: "eaq_health_literacy",
        text: "How confident are you in filling out medical forms by yourself?",
        options: ["Not confident at all", "A little confident", "Somewhat confident", "Quite confident", "Extremely confident", "Decline to answer"],
      },
    ],
  },
  {
    id: "healthcare_access",
    title: "Domain 3: Health Care Access and Quality",
    description: "Access to and quality of health care services, including primary care and insurance.",
    questions: [
      {
        id: "hcaq_insurance",
        text: "Do you have health insurance?",
        options: ["Yes", "No", "Don't know", "Decline to answer"],
      },
      {
        id: "hcaq_transportation",
        text: "In the past 12 months, has a lack of reliable transportation kept you from medical appointments, work, or getting things needed for daily living?",
        options: ["Yes", "No", "Decline to answer"],
      },
      {
        id: "hcaq_pcp",
        text: "Do you have a primary care provider you see for regular check-ups?",
        options: ["Yes", "No", "Don't know", "Decline to answer"],
      },
    ],
  },
  {
    id: "neighborhood_environment",
    title: "Domain 4: Neighborhood and Built Environment",
    description: "Safety and health of an individual's living environment.",
    questions: [
      {
        id: "nbe_housing",
        text: "What is your living situation today?",
        options: ["I have a steady place to live", "I have a place to live, but I am worried about losing it", "I do not have a steady place to live", "Decline to answer"],
      },
      {
        id: "nbe_housing_problems",
        text: "Do you have any problems with your housing? (Select all that apply)",
        multiSelect: true,
        options: ["Pests (bugs, mice)", "Mold", "Lack of heat", "Stove or oven not working", "Water leaks", "None of the above", "Decline to answer"],
      },
      {
        id: "nbe_safety",
        text: "How safe do you feel in your neighborhood?",
        options: ["Very safe", "Somewhat safe", "A little unsafe", "Very unsafe", "Decline to answer"],
      },
    ],
  },
  {
    id: "social_community",
    title: "Domain 5: Social and Community Context",
    description: "Social support networks, community engagement, and experiences with discrimination or violence.",
    questions: [
      {
        id: "scc_social_support",
        text: "How often do you get the social and emotional support you need?",
        options: ["Always", "Usually", "Sometimes", "Rarely", "Never", "Decline to answer"],
      },
      {
        id: "scc_safety_threat",
        text: "In the past 12 months, have you felt physically or emotionally threatened by anyone?",
        options: ["Never", "Rarely", "Sometimes", "Often", "Decline to answer"],
      },
      {
        id: "scc_wants_help",
        text: "Do you want help with any of these problems?",
        options: ["Yes", "No"],
      },
    ],
  },
] as const;

type QuestionDef = (typeof SDOH_DOMAINS)[number]["questions"][number];

function SdohRadioQuestion({
  question,
  value,
  onChange,
}: {
  question: QuestionDef;
  value: string;
  onChange: (val: string) => void;
}) {
  return (
    <div className="space-y-2 rounded-md border p-4">
      <p className="text-sm font-medium leading-snug">{question.text}</p>
      <RadioGroup value={value} onValueChange={onChange} className="mt-2 space-y-1.5">
        {question.options.map((opt) => (
          <div key={opt} className="flex items-center space-x-2">
            <RadioGroupItem value={opt} id={`${question.id}-${opt}`} />
            <label htmlFor={`${question.id}-${opt}`} className="text-sm cursor-pointer">
              {opt}
            </label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
}

function SdohMultiSelectQuestion({
  question,
  values,
  onChange,
}: {
  question: QuestionDef;
  values: string[];
  onChange: (vals: string[]) => void;
}) {
  const toggle = (opt: string) => {
    const updated = values.includes(opt)
      ? values.filter((v) => v !== opt)
      : [...values, opt];
    onChange(updated);
  };

  return (
    <div className="space-y-2 rounded-md border p-4">
      <p className="text-sm font-medium leading-snug">{question.text}</p>
      <div className="mt-2 space-y-1.5">
        {question.options.map((opt) => (
          <div key={opt} className="flex items-center space-x-2">
            <Checkbox
              id={`${question.id}-${opt}`}
              checked={values.includes(opt)}
              onCheckedChange={() => toggle(opt)}
            />
            <label htmlFor={`${question.id}-${opt}`} className="text-sm cursor-pointer">
              {opt}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SdohSection({ form }: SdohSectionProps) {
  const screenings = form.watch("sdoh_screenings") || [];

  // Parse stored screenings: each entry is "questionId::answer" or "questionId::answer1||answer2" for multi
  const getAnswer = (questionId: string): string => {
    const entry = screenings.find((s) => s.startsWith(`${questionId}::`));
    return entry ? entry.split("::")[1] : "";
  };

  const getMultiAnswers = (questionId: string): string[] => {
    const entry = screenings.find((s) => s.startsWith(`${questionId}::`));
    if (!entry) return [];
    const val = entry.split("::")[1];
    return val ? val.split("||") : [];
  };

  const setAnswer = (questionId: string, answer: string) => {
    const current = form.getValues("sdoh_screenings") || [];
    const filtered = current.filter((s) => !s.startsWith(`${questionId}::`));
    if (answer) {
      filtered.push(`${questionId}::${answer}`);
    }
    form.setValue("sdoh_screenings", filtered, { shouldDirty: true });
  };

  const setMultiAnswers = (questionId: string, answers: string[]) => {
    const current = form.getValues("sdoh_screenings") || [];
    const filtered = current.filter((s) => !s.startsWith(`${questionId}::`));
    if (answers.length > 0) {
      filtered.push(`${questionId}::${answers.join("||")}`);
    }
    form.setValue("sdoh_screenings", filtered, { shouldDirty: true });
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold border-b pb-2">
          Social Determinants of Health (SDOH) Screening
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Based on AHC-HRSN, PRAPARE, and Healthy People 2030 frameworks
        </p>
      </div>

      <Alert variant="default" className="bg-muted">
        <HeartHandshake className="h-4 w-4" />
        <AlertDescription>
          These questions help identify needs beyond medical care so we can connect you with
          community resources. All responses are confidential.
        </AlertDescription>
      </Alert>

      {SDOH_DOMAINS.map((domain) => (
        <div key={domain.id} className="space-y-4">
          <div>
            <h4 className="font-semibold text-foreground">{domain.title}</h4>
            <p className="text-sm text-muted-foreground">{domain.description}</p>
          </div>
          <div className="space-y-3">
            {domain.questions.map((q) =>
              "multiSelect" in q && q.multiSelect ? (
                <SdohMultiSelectQuestion
                  key={q.id}
                  question={q}
                  values={getMultiAnswers(q.id)}
                  onChange={(vals) => setMultiAnswers(q.id, vals)}
                />
              ) : (
                <SdohRadioQuestion
                  key={q.id}
                  question={q}
                  value={getAnswer(q.id)}
                  onChange={(val) => setAnswer(q.id, val)}
                />
              )
            )}
          </div>
        </div>
      ))}

      <FormField
        control={form.control}
        name="sdoh_notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Additional Details (Optional)</FormLabel>
            <FormDescription>
              Share any details about the items above, or other needs not listed.
            </FormDescription>
            <FormControl>
              <Textarea
                placeholder="Any additional information about your social needs..."
                className="min-h-[80px]"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
